import { inject, Injectable, signal, TemplateRef, WritableSignal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { v7 as uuid } from 'uuid';

export interface DialogData {
  id: string;
  content: TemplateRef<any>;
  config: DialogConfig;
  templateContext: {
    confirm: () => void;
    cancel: () => void;
  };
}

export interface DialogConfig {
  isVisible?: boolean;
  title?: string;
  message?: string;
  showClose?: boolean;
  onCancel?: () => void; // Callback for cancel action
  onConfirm?: () => void; // Callback for confirm action
  okText?: string;
  showButtons?: boolean;
  showOkButton?: boolean;
  showButtonIcons?: boolean;
  rightAlignedButtons?: boolean;
  size?: 'medium' | 'large' | 'x-large' | 'xx-large' | 'large-scroll';
  position?: 'top' | 'center' | 'middle';
  styleClass?: string;
  hideSaveButton?: boolean;
  preventDuplicates?: boolean;
  /** Keep the dialog open when the route changes. By default dialogs are dismissed on navigation. */
  keepOpenOnNavigation?: boolean;
}

const DEFAULT_CONFIG: DialogConfig = {
  isVisible: false,
  title: '',
  message: '',
  showClose: false,
  okText: 'OK',
  showButtons: true,
  showOkButton: false,
  showButtonIcons: false,
  rightAlignedButtons: false,
  size: 'medium',
  position: 'top',
  hideSaveButton: false,
  preventDuplicates: true,
  keepOpenOnNavigation: false,
};

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private _dialogStack: WritableSignal<DialogData[]> = signal([]);
  public get dialogStack() {
    return this._dialogStack;
  }

  constructor() {
    // A dialog belongs to the page that opened it. When the route changes (link, router.navigate or
    // browser back/forward) dismiss it as if the user closed it, so its cancel callback runs before the
    // next page initialises and it is not left open over a page it no longer belongs to.
    inject(Router).events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => this.dismissAllOnNavigation());
  }

  private dismissAllOnNavigation(): void {
    // Newest first, mirroring how a user would close stacked dialogs.
    [...this._dialogStack()]
      .reverse()
      .filter((dialog) => !dialog.config.keepOpenOnNavigation)
      .forEach((dialog) => dialog.templateContext.cancel());
  }

  public openDialog(
    content: DialogData['content'],
    customConfig: Partial<DialogConfig>,
    onConfirm: () => void,
    onCancel: () => void
  ): string {
    const dialogId = uuid();
    const config: DialogConfig = { ...DEFAULT_CONFIG, ...customConfig };
    const currentStack = this._dialogStack();

    if (config.preventDuplicates) {
      const existing = currentStack.find((dialog) => dialog.content === content);
      if (existing) {
        return existing.id;
      }
    }

    const newDialog: DialogData = {
      id: dialogId,
      content,
      config,
      templateContext: {
        confirm: () => {
          this.closeDialog(dialogId);
          onConfirm();
        },
        cancel: () => {
          this.closeDialog(dialogId);
          onCancel();
        },
      },
    };
    this._dialogStack.set([...currentStack, newDialog]);
    return dialogId;
  }

  public closeDialog(dialogId: DialogData['id']): void {
    const currentStack = this._dialogStack();
    const updatedStack = currentStack.filter(
      (dialog) => dialog.id !== dialogId
    );
    this._dialogStack.set(updatedStack);
  }
}
